import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { organizationKeys } from '../query-keys';
import { toast } from 'sonner';
import { toastApiError } from './toast-api-error';
import i18next from '@/lib/i18n';
import {
  orgLogoRequestPresignedUrlContract,
  orgLogoConfirmUploadContract,
  deleteOrgLogoContract,
  type OrgLogoRequestPresignedUrlResponse,
  type OrgLogoConfirmUploadRequest,
  type OrgLogoConfirmUploadResponse,
  type DeleteOrgLogoResponse
} from '@bliks-io/contracts';

/**
 * Request Presigned URL mutation for org logo
 *
 * Requests a presigned URL for uploading an organization logo directly to DigitalOcean Spaces.
 * This is the first step in the org logo upload flow.
 *
 * @example
 * ```tsx
 * function OrgLogoUpload() {
 *   const requestUrl = useRequestOrgLogoUploadUrl();
 *
 *   const handleUpload = async (file: File) => {
 *     try {
 *       const presignedData = await requestUrl.mutateAsync();
 *       // Use presignedData to upload file to S3
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return <Button loading={requestUrl.isPending}>Upload Logo</Button>;
 * }
 * ```
 */
export function useRequestOrgLogoUploadUrl() {
  return useMutation({
    mutationFn: async (): Promise<OrgLogoRequestPresignedUrlResponse> => {
      const { data } = await apiClient.post<OrgLogoRequestPresignedUrlResponse>(
        orgLogoRequestPresignedUrlContract.path,
        {}
      );
      return data;
    },
    onError: (error) => {
      toastApiError(error, 'logo.prepareError');
    }
  });
}

/**
 * Confirm Org Logo Upload mutation
 *
 * Confirms the org logo upload and triggers server-side processing.
 * This is the final step after uploading the file to the presigned URL.
 * The server will process the image/SVG appropriately.
 *
 * @example
 * ```tsx
 * function OrgLogoUpload() {
 *   const confirmUpload = useConfirmOrgLogoUpload();
 *
 *   const handleConfirm = async (key: string, mimeType: string) => {
 *     try {
 *       const result = await confirmUpload.mutateAsync({ key, mimeType });
 *       toast.success(i18next.t('logo.uploadSuccess'));
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return <Button loading={confirmUpload.isPending}>Confirm Upload</Button>;
 * }
 * ```
 */
export function useConfirmOrgLogoUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: OrgLogoConfirmUploadRequest
    ): Promise<OrgLogoConfirmUploadResponse> => {
      const { data } = await apiClient.post<OrgLogoConfirmUploadResponse>(
        orgLogoConfirmUploadContract.path,
        request
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate organization to refetch with new logo
      queryClient.invalidateQueries({ queryKey: organizationKeys.details() });

      toast.success(i18next.t('logo.uploadSuccess'));
    },
    onError: (error) => {
      toastApiError(error, 'logo.uploadError');
    }
  });
}

/**
 * Delete Org Logo mutation
 *
 * Deletes the organization's logo and all associated files from storage.
 *
 * @example
 * ```tsx
 * function OrgLogoUpload() {
 *   const deleteLogo = useDeleteOrgLogo();
 *
 *   const handleDelete = async () => {
 *     try {
 *       await deleteLogo.mutateAsync();
 *       toast.success(i18next.t('logo.deleteSuccess'));
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return <Button loading={deleteLogo.isPending}>Delete Logo</Button>;
 * }
 * ```
 */
export function useDeleteOrgLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DeleteOrgLogoResponse> => {
      const { data } = await apiClient.delete<DeleteOrgLogoResponse>(deleteOrgLogoContract.path);
      return data;
    },
    onSuccess: () => {
      // Invalidate organization to refetch without logo
      queryClient.invalidateQueries({ queryKey: organizationKeys.details() });

      toast.success(i18next.t('logo.deleteSuccess'));
    },
    onError: (error) => {
      toastApiError(error, 'logo.deleteError');
    }
  });
}
